import React, { useEffect, useState, useCallback } from 'react';
import { ExtractedLink } from '../utils/linkExtractor';

interface LinkPrefetcherProps {
  links: ExtractedLink[];
  onMetadataExtracted?: (url: string, title: string, description?: string) => void;
}

// Store the extracted metadata across renders
const extractedMetadata: Record<string, { title: string; description?: string }> = {};

// Add a counter to track metadata updates for forcing re-renders
let metadataUpdateCounter = 0;
const metadataListeners: Set<() => void> = new Set();

// Track per-URL prefetch retry counts to avoid infinite retry loops
const prefetchRetryCounts: Record<string, number> = {};
// Maximum number of times to retry fetching a URL before giving up
const MAX_PREFETCH_RETRIES = 3;

// Helper to notify all listeners when metadata updates
const notifyMetadataUpdate = () => {
  metadataUpdateCounter++;
  console.debug(`notifyMetadataUpdate: Counter incremented to ${metadataUpdateCounter}, notifying ${metadataListeners.size} listeners`);
  metadataListeners.forEach(listener => {
    try {
      listener();
    } catch (error) {
      console.error('Error in metadata listener:', error);
    }
  });
};

/**
 * Component that handles prefetching of important links in the background
 * to improve loading times when users click on them and extract metadata
 */
export const LinkPrefetcher: React.FC<LinkPrefetcherProps> = ({ links, onMetadataExtracted }) => {
  const [prefetchedUrls, setPrefetchedUrls] = useState<Set<string>>(new Set());

  // Function to extract metadata from HTML content
  const extractMetadataFromHtml = useCallback((html: string, url: string): { title: string; description?: string } => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Try to get title from various sources in priority order
      let title = 
        doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="twitter:title"]')?.getAttribute('content') ||
        doc.querySelector('title')?.textContent ||
        '';
      
      // Try to get description from various sources
      let description = 
        doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="description"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="twitter:description"]')?.getAttribute('content') || 
        undefined;
      
      // Special handling for Medicare LCD/NCD pages
      if (url.includes('cms.gov')) {
        // Extract LCD/NCD number from URL first
        let documentType = '';
        let documentNumber = '';
        
        if (url.includes('lcd.aspx')) {
          documentType = 'LCD';
          const match = url.match(/lcdid=(\d+)/i);
          if (match) documentNumber = match[1];
        } else if (url.includes('ncd.aspx')) {
          documentType = 'NCD';
          const match = url.match(/ncdid=(\d+)/i);
          if (match) documentNumber = match[1];
        }
        
        // Look for the actual document title in the page with comprehensive selectors
        const mainTitle = doc.querySelector('#ctl00_ctl00_ctl00_CMSBreadCrumbsTitle_CMSBreadCrumbsTitle_CMSBreadCrumbsTitle_lblTitle')?.textContent?.trim();
        const lcdTitle = doc.querySelector('.lcd-title')?.textContent?.trim();
        const pageTitleElement = doc.querySelector('title');
        const pageTitle = pageTitleElement?.textContent?.trim();
        const pageHeading = doc.querySelector('h1, .heading-text')?.textContent?.trim();
        
        // Try additional selectors for Medicare document titles
        const contentTitle = doc.querySelector('#content-title, .content-title')?.textContent?.trim();
        const documentTitle = doc.querySelector('.document-title, #document-title')?.textContent?.trim();
        const breadcrumbTitle = doc.querySelector('.breadcrumb-title, .cms-title')?.textContent?.trim();
        
        // Also try to extract from the page's main content area
        const mainContentTitle = doc.querySelector('main h1, .main-content h1, .page-title')?.textContent?.trim();
        
        // Find the best title from available options, prioritizing actual page content over generic titles
        const bestTitle = contentTitle || documentTitle || mainContentTitle || breadcrumbTitle || 
                         mainTitle || lcdTitle || pageHeading || 
                         ((pageTitle && !pageTitle.includes('Medicare Coverage Database')) ? pageTitle : null);
        
        // If we found a good title on the page
        if (bestTitle) {
          title = bestTitle;
          console.debug(`LinkPrefetcher: Found page title for ${url}:`, bestTitle);
        } 
        // If we at least know the document type and number
        else if (documentType && documentNumber) {
          title = `${documentType} ${documentNumber} - Medicare Coverage Document`;
          console.debug(`LinkPrefetcher: Using fallback title for ${url}:`, title);
        }
        
        // Look for LCD/NCD specific content for enhanced descriptions
        const lcdContent = doc.querySelector('.lcd-info');
        if (lcdContent) {
          // Try to extract medical topics/conditions
          const conditions = Array.from(doc.querySelectorAll('.lcd-conditions li'))
            .map(el => el.textContent?.trim())
            .filter(Boolean);
          
          if (conditions.length > 0) {
            description = `Coverage for: ${conditions.join(', ')}`;
          }
        }
        
        // Try additional Medicare-specific description sources if we don't have one yet
        if (!description) {
          // Look for summary or description content in various common Medicare page elements
          const summaryText = 
            doc.querySelector('.summary')?.textContent?.trim() ||
            doc.querySelector('.description')?.textContent?.trim() ||
            doc.querySelector('#lblSummary')?.textContent?.trim() ||
            doc.querySelector('.ncd-summary')?.textContent?.trim() ||
            doc.querySelector('.lcd-summary')?.textContent?.trim();
            
          if (summaryText && summaryText.length > 10 && summaryText.length < 500) {
            description = summaryText;
          }
        }
        
        // If we still don't have a description but know document type
        if (!description && documentType) {
          description = documentType === 'LCD' 
            ? `Local Coverage Determination (LCD) ${documentNumber} - Medicare coverage policy document`
            : `National Coverage Determination (NCD) ${documentNumber} - Medicare coverage policy document`;
        }
      }
      
      return { 
        title: title || new URL(url).hostname,
        description 
      };
    } catch (error) {
      console.warn('Failed to extract metadata:', error);
      return { title: new URL(url).hostname };
    }
  }, []);

  useEffect(() => {
    // Find Medicare LCD/NCD URLs to prefetch
    const medicareUrls = links
      .filter(link => 
        !prefetchedUrls.has(link.url) &&
        link.url.includes('cms.gov') &&
        (link.url.includes('lcd.aspx') || link.url.includes('ncd.aspx'))
      )
      .map(link => link.url);

    if (medicareUrls.length === 0) return;

    // Only prefetch a small number at a time to avoid excessive network requests
    const urlsToProcess = medicareUrls.slice(0, 3);

    const prefetch = async () => {
      const newPrefetchedUrls = new Set(prefetchedUrls);
      
      // Process URLs concurrently but track completion
      const promises = urlsToProcess.map(url => {
        return new Promise<void>((resolve) => {
          try {
            // Always use XMLHttpRequest to fetch content and extract metadata
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.responseType = 'text';
            xhr.onload = () => {
              if (xhr.status === 200) {
                newPrefetchedUrls.add(url);
                console.info(`Prefetched and processed: ${url}`);
                
                try {
                  // Extract metadata from the response
                  const { title, description } = extractMetadataFromHtml(xhr.responseText, url);
                  
                  // Store the metadata in our global object
                  extractedMetadata[url] = { title, description };
                  console.debug(`LinkPrefetcher: About to call notifyMetadataUpdate for ${url}`);
                  
                  // Notify all registered listeners about the metadata update
                  notifyMetadataUpdate();
                  console.debug(`LinkPrefetcher: Stored metadata for ${url}:`, { title, description });
                  
                  // Notify the parent component if callback provided
                  if (onMetadataExtracted && title) {
                    // Call the callback immediately
                    onMetadataExtracted(url, title, description);
                    console.info(`Extracted metadata for ${url}: ${title}`);
                    
                    // Create synthetic DOM event to notify other components that might be listening
                    console.debug('LinkPrefetcher: About to dispatch metadataExtracted event for', url, 'with data:', { title, description });
                    const event = new CustomEvent('metadataExtracted', { 
                      detail: { url, title, description }
                    });
                    document.dispatchEvent(event);
                    console.debug('LinkPrefetcher: Successfully dispatched metadataExtracted event');
                  }
                } catch (error) {
                  console.warn(`Failed to extract metadata for ${url}:`, error);
                }
                resolve();
              } else {
                console.warn(`XHR failed with status ${xhr.status}: ${url}`);
                // Increment retry count and give up after MAX_PREFETCH_RETRIES
                prefetchRetryCounts[url] = (prefetchRetryCounts[url] || 0) + 1;
                if (prefetchRetryCounts[url] >= MAX_PREFETCH_RETRIES) {
                  console.warn(`Giving up prefetching ${url} after ${prefetchRetryCounts[url]} attempts`);
                  // Mark as prefetched to avoid retrying forever
                  newPrefetchedUrls.add(url);
                  // Provide a basic fallback metadata so UI can show something useful
                  extractedMetadata[url] = extractedMetadata[url] || { title: new URL(url).hostname, description: `Failed to prefetch after ${prefetchRetryCounts[url]} attempts` };
                  notifyMetadataUpdate();
                }
                resolve(); // Still resolve to not block other URLs
              }
            };
            xhr.onerror = () => {
              console.warn(`XHR prefetch failed: ${url}`);
              // Network error - increment retry and give up if exceeded
              prefetchRetryCounts[url] = (prefetchRetryCounts[url] || 0) + 1;
              if (prefetchRetryCounts[url] >= MAX_PREFETCH_RETRIES) {
                console.warn(`Giving up prefetching ${url} after ${prefetchRetryCounts[url]} attempts (network error)`);
                newPrefetchedUrls.add(url);
                extractedMetadata[url] = extractedMetadata[url] || { title: new URL(url).hostname, description: `Network error: failed to prefetch after ${prefetchRetryCounts[url]} attempts` };
                notifyMetadataUpdate();
              }
              resolve(); // Still resolve to not block other URLs
            };
            xhr.send();
            
            // Also add browser-level prefetch hint for better caching
            try {
              const link = document.createElement('link');
              link.rel = 'prefetch';
              link.href = url;
              link.as = 'document';
              document.head.appendChild(link);
            } catch (prefetchError) {
              console.warn(`Browser prefetch hint failed: ${prefetchError}`);
            }
          } catch (error) {
            console.warn(`Failed to prefetch ${url}:`, error);
            // Treat as a failure and increment retry count
            prefetchRetryCounts[url] = (prefetchRetryCounts[url] || 0) + 1;
            if (prefetchRetryCounts[url] >= MAX_PREFETCH_RETRIES) {
              console.warn(`Giving up prefetching ${url} after ${prefetchRetryCounts[url]} attempts (exception)`);
              newPrefetchedUrls.add(url);
              extractedMetadata[url] = extractedMetadata[url] || { title: new URL(url).hostname, description: `Exception: failed to prefetch after ${prefetchRetryCounts[url]} attempts` };
              notifyMetadataUpdate();
            }
            resolve(); // Still resolve to not block other URLs
          }
        });
      });
      
      // Wait for all prefetch operations to complete
      await Promise.all(promises);
      
      // Update the prefetched URLs state after all operations complete only if changed
      const setsEqual = (a: Set<string>, b: Set<string>) => {
        if (a.size !== b.size) return false;
        for (const v of a) if (!b.has(v)) return false;
        return true;
      };

      if (!setsEqual(prefetchedUrls, newPrefetchedUrls)) {
        setPrefetchedUrls(newPrefetchedUrls);
      } else {
        // No changes to prefetched URLs - this prevents re-triggering the effect unnecessarily
        console.debug('LinkPrefetcher: No change to prefetched URLs after attempt');
      }
    };

    // Start prefetching after a small delay to avoid impacting initial page load
    const timeoutId = setTimeout(prefetch, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [links, prefetchedUrls]);

  // This component doesn't render anything
  return null;
};

// Static method to get extracted metadata from anywhere in the application
export const getExtractedMetadata = (url: string): { title?: string; description?: string } => {
  return extractedMetadata[url] || {};
};

// Hook to subscribe to metadata updates and force re-render when metadata changes
export const useMetadataUpdates = () => {
  const [updateCounter, setUpdateCounter] = React.useState(metadataUpdateCounter);
  
  React.useEffect(() => {
    const listener = () => {
      console.debug('useMetadataUpdates: Metadata update received, triggering re-render');
      setUpdateCounter(metadataUpdateCounter);
    };
    
    metadataListeners.add(listener);
    console.debug(`useMetadataUpdates: Registered listener, total listeners: ${metadataListeners.size}`);
    
    return () => {
      metadataListeners.delete(listener);
      console.debug(`useMetadataUpdates: Unregistered listener, total listeners: ${metadataListeners.size}`);
    };
  }, []);
  
  // Also listen for the custom DOM event as a backup
  React.useEffect(() => {
    const handleMetadataEvent = (event: CustomEvent) => {
      console.debug('useMetadataUpdates: DOM event received', event.detail);
      setUpdateCounter(prev => prev + 1);
    };
    
    document.addEventListener('metadataExtracted', handleMetadataEvent as EventListener);
    
    return () => {
      document.removeEventListener('metadataExtracted', handleMetadataEvent as EventListener);
    };
  }, []);
  
  return updateCounter;
};

// Utility to check if metadata exists for a URL
export const hasExtractedMetadata = (url: string): boolean => {
  return Boolean(extractedMetadata[url]);
};

export default LinkPrefetcher;